<template>
  <div class="flex items-start gap-3">
    <label
      :for="id"
      class="flex items-start gap-3 cursor-pointer w-full"
      :data-test-id="`${id}-label`"
    >
      <RadioButton
        v-bind="$attrs"
        :modelValue="internalValue"
        :value="value"
        :disabled="disabled"
        :id="id"
        :name="name"
        :data-test-id="`${id}-radio`"
        @update:modelValue="handleChange"
        @focus="handleFocus"
        @blur="handleBlur"
        :class="radioClasses"
        :pt="{
          root: { class: 'flex items-center justify-center h-5 w-auto', tabindex: '0' },
          input: { tabindex: '-1' },
          hiddenInput: { tabindex: '-1' },
          box: {
            class: `w-5 h-5 rounded-full ${boxClasses} flex-shrink-0`,
          },
          icon: { class: 'hidden' },
        }"
        :aria-label="label || 'Radio option'"
        @keydown.enter.prevent="selectOption"
        @keydown.space.prevent="selectOption"
      />

      <div class="flex flex-col gap-1 flex-1">
        <span class="!font-just !text-base transition-all duration-200 flex gap-1" :class="labelClasses">
          <template v-if="required">
            <span v-if="focused || internalValue === value">*</span>{{ label
            }}<span v-if="!focused && internalValue !== value">*</span>
          </template>
          <template v-else>
            {{ label }}
          </template>
        </span>

        <span v-if="description" class="text-sm text-gray-500 !font-just">
          {{ description }}
        </span>
      </div>
    <Message
      v-if="invalid && errorMessage"
      severity="error"
      size="small"
      variant="simple"
      :class="{ '!text-error !px-[10px] !mt-[3.84px]': invalid }"
      :data-test-id="`${id}-errorMessage`"
    >
      {{ errorMessage }}
    </Message>
    </label>
  </div>
</template>

<script setup>
// Forward fallthrough attrs (tabindex, @focus/@click) onto the inner control via
// v-bind="$attrs", not the wrapper, so a parent-supplied tabindex (e.g. from
// CustomField) doesn't create a second tab stop. See BaseInput.vue.
defineOptions({ inheritAttrs: false });

import { RadioButton } from "primevue";
import { ref, computed, watch } from "vue";

const props = defineProps({
  modelValue: { type: [String, Number, Boolean], default: null },
  value: { type: [String, Number, Boolean], required: true },
  label: { type: String, default: "" },
  description: { type: String, default: "" },
  id: { type: String, default: "" },
  name: { type: String, default: "radio" },
  required: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
  invalid: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "change", "focus", "blur"]);
const focused = ref(false);
const internalValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  (val) => {
    if (val !== internalValue.value) internalValue.value = val;
  }
);

const handleChange = (value) => {
  internalValue.value = value;
  emit("update:modelValue", value);
  emit("change", value);
};

const handleFocus = () => {
  focused.value = true;
  emit("focus");
};

const handleBlur = (event) => {
  focused.value = false;
  emit("blur", event);
};

const selectOption = () => {
  handleChange(props.value);
};

const boxClasses = computed(() => {
  if (props.invalid) {
    return "!border-error";
  }
  if (focused.value || internalValue.value === props.value) {
    return "!bg-deepSky !rounded-full !border-[1px] !border-turquoise";
  }
  return "!border-softGray";
});

const radioClasses = computed(() => ({
  "!border-deepSky":
    (focused.value || internalValue.value === props.value) && !props.invalid,
  "!border-softGray":
    !focused.value && internalValue.value !== props.value && !props.invalid,
  "!border-error": props.invalid,
}));

const labelClasses = computed(() => ({
  "!text-deepSky":
    (focused.value || internalValue.value === props.value) && !props.invalid,
  "!text-greyBlue":
    !focused.value && internalValue.value !== props.value && !props.invalid,
  "!text-error": props.invalid,
}));
</script>

<style>
:root {
  --p-radiobutton-checked-background: deepSky !important;
  --p-radiobutton-checked-border-color: deepSky !important;
  --p-radiobutton-checked-hover-border-color: deepSky !important;
  --p-radiobutton-checked-hover-background: deepSky !important;
}
</style>
